import { lazy, Suspense, useState, createContext, useContext } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { useCartSync } from "@/hooks/useCartSync";
import { usePageTracking } from "@/hooks/usePageTracking";
import { SupportChat } from "@/components/SupportChat";
import { ThemeApplicator } from "@/components/ThemeApplicator";
import { SeoManager } from "@/components/SeoManager";
import { LaunchPromoBanner } from "@/components/LaunchPromoBanner";
import { LaunchPromoModal } from "@/components/LaunchPromoModal";
import { AnimatePresence, motion } from "framer-motion";

// Retry dynamic imports once, then force a hard reload so users get the latest
// bundle after a redeploy (avoids "Importing a module script failed" white screens).
const lazyWithRetry = <T,>(factory: () => Promise<{ default: React.ComponentType<T> }>) =>
  lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const reloadKey = "__chunk_reload__";
      const alreadyReloaded = sessionStorage.getItem(reloadKey);
      if (!alreadyReloaded) {
        sessionStorage.setItem(reloadKey, "1");
        window.location.reload();
        // Return a never-resolving promise while reloading
        return new Promise(() => {}) as never;
      }
      throw err;
    }
  });

const DemoPage = lazyWithRetry(() => import("./pages/DemoPage"));
const Index = lazyWithRetry(() => import("./pages/Index"));
const ProductDetail = lazyWithRetry(() => import("./pages/ProductDetail"));
const BrandPage = lazyWithRetry(() => import("./pages/BrandPage"));
const AuthPage = lazyWithRetry(() => import("./pages/AuthPage"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const ProfilePage = lazyWithRetry(() => import("./pages/ProfilePage"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const ShippingPolicy = lazyWithRetry(() => import("./pages/ShippingPolicy"));
const AdminPage = lazyWithRetry(() => import("./pages/AdminPage"));
const QuizPage = lazyWithRetry(() => import("./pages/QuizPage"));
const ConfiguratorPage = lazyWithRetry(() => import("./pages/ConfiguratorPage"));
const UnsubscribePage = lazyWithRetry(() => import("./pages/UnsubscribePage"));
const TutorialPage = lazyWithRetry(() => import("./pages/TutorialPage"));
const MaterialPage = lazyWithRetry(() => import("./pages/MaterialPage"));
const CatalogPage = lazyWithRetry(() => import("./pages/CatalogPage"));
const CategoryPage = lazyWithRetry(() => import("./pages/CategoryPage"));
const CollectionPage = lazyWithRetry(() => import("./pages/CollectionPage"));
const TrackOrderPage = lazyWithRetry(() => import("./pages/TrackOrderPage"));
const DaniPage = lazyWithRetry(() => import("./pages/DaniPage"));
const RefurbishedPage = lazyWithRetry(() => import("./pages/RefurbishedPage"));

// Clear the reload flag once the app has successfully mounted.
if (typeof window !== "undefined") {
  window.addEventListener("load", () => sessionStorage.removeItem("__chunk_reload__"));
}

const queryClient = new QueryClient();

// Set to false to open the site to the public
const PRE_LAUNCH = false;
const STORE_ACCESS_KEY = "skinvault_store_access";

export const StoreAccessContext = createContext<{
  hasAccess: boolean;
  grant: (pw: string) => Promise<boolean>;
}>({
  hasAccess: false,
  grant: async () => false,
});

export const useStoreAccessContext = () => useContext(StoreAccessContext);

function useStoreAccess() {
  const [hasAccess, setHasAccess] = useState(() => sessionStorage.getItem(STORE_ACCESS_KEY) === "granted");
  const grant = async (pw: string) => {
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase.functions.invoke("verify-store-password", {
        body: { password: pw },
      });
      if (!error && data?.ok === true) {
        sessionStorage.setItem(STORE_ACCESS_KEY, "granted");
        setHasAccess(true);
        return true;
      }
    } catch {
      // fall through to false
    }
    return false;
  };
  return { hasAccess, grant };
}

const pageVariants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.2, ease: [0.4, 0, 1, 1] as [number, number, number, number] } },
};

const RouteLoadingFallback = () => (
  <div className="min-h-screen bg-background" role="status" aria-live="polite">
    <div className="h-16 border-b border-border/60 bg-background/95" />
    <main className="container mx-auto px-4 py-10">
      <div className="grid gap-10 lg:grid-cols-2 lg:items-center animate-pulse">
        <div className="space-y-5">
          <div className="h-6 w-36 rounded-full bg-muted" />
          <div className="space-y-3">
            <div className="h-12 w-full max-w-lg rounded-xl bg-muted" />
            <div className="h-12 w-4/5 max-w-md rounded-xl bg-muted" />
          </div>
          <div className="h-5 w-full max-w-md rounded bg-muted" />
          <div className="h-11 w-40 rounded-lg bg-muted" />
        </div>
        <div className="aspect-[4/3] w-full rounded-3xl bg-muted" />
      </div>
    </main>
    <span className="sr-only">Chargement de la page…</span>
  </div>
);

const AnimatedRoutes = ({ storeUnlocked }: { storeUnlocked: boolean }) => {
  const location = useLocation();
  const showAllRoutes = !PRE_LAUNCH || storeUnlocked;
  return (
    <AnimatePresence mode="wait">
      <motion.div key={location.pathname} variants={pageVariants} initial="initial" animate="animate" exit="exit" className="will-change-transform">
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes location={location}>
            <Route path="/" element={!PRE_LAUNCH || storeUnlocked ? <Index /> : <DemoPage />} />
            {/* Admin & auth always accessible */}
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/tutorial" element={<TutorialPage />} />
            <Route path="/track" element={<TrackOrderPage />} />
            <Route path="/dani" element={<DaniPage />} />
            <Route path="/refurbished" element={<RefurbishedPage />} />
            {showAllRoutes ? (
              <>
                <Route path="/shop" element={<Navigate to="/catalog" replace />} />
                <Route path="/catalog" element={<CatalogPage />} />
                <Route path="/category/gaming" element={<Navigate to="/category/consoles" replace />} />
                <Route path="/category/controllers" element={<Navigate to="/category/consoles" replace />} />
                <Route path="/category/card-skins" element={<Navigate to="/category/cards" replace />} />
                <Route path="/category/:slug" element={<CategoryPage />} />
                <Route path="/collection/:slug" element={<CollectionPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/shipping" element={<ShippingPolicy />} />
                <Route path="/quiz" element={<QuizPage />} />
                <Route path="/configurator" element={<ConfiguratorPage />} />
                <Route path="/material/:material" element={<MaterialPage />} />
                <Route path="/:categorySlug/:brandSlug" element={<BrandPage />} />
                <Route path="/product/:handle" element={<ProductDetail />} />
                <Route path="*" element={<NotFound />} />
              </>
            ) : (
              <Route path="*" element={<Navigate to="/" replace />} />
            )}
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
};

const AppInner = ({ storeUnlocked }: { storeUnlocked: boolean }) => {
  usePageTracking();
  return (
    <>
      <ThemeApplicator />
      <SeoManager />
      <LaunchPromoBanner />
      <AnimatedRoutes storeUnlocked={storeUnlocked} />
      <SupportChat />
      <LaunchPromoModal />
    </>
  );
};

const AppContent = () => {
  useCartSync();
  const access = useStoreAccess();
  return (
    <StoreAccessContext.Provider value={access}>
      <BrowserRouter>
        <AppInner storeUnlocked={access.hasAccess} />
      </BrowserRouter>
    </StoreAccessContext.Provider>
  );
};

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AppContent />
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
